import React, { useEffect, useState } from 'react'
import Header from './components/Header'
import FileUploader from './components/FileUploader'
import EmailList from './components/EmailList'
import ModalView from './components/ModalView'
import Toast from './components/Toast'
import { fetchFiles, analyzeEmail, deleteFile } from './api'

const App = () => {
  const [files, setFiles] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [analysis, setAnalysis] = useState(null)
  const [selectedEmail, setSelectedEmail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })

  const loadFiles = async () => {
    try {
      setLoading(true)
      const data = await fetchFiles()
      setFiles(data.files || [])
    } catch (e) {
      console.error('Could not fetch files', e)
      setFiles([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadFiles()
  }, [])

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000)
  }

  const viewAnalysis = async (file) => {
    setModalOpen(true)
    setSelectedEmail(file)
    setAnalysis(null)

    try {
      const result = await analyzeEmail(file.filename)
      setAnalysis(result.result || result)
    } catch (e) {
      console.error('Analysis failed', e)
      setAnalysis({ error: String(e) })
    }
  }

  const handleDelete = async (filename) => {
    try {
      await deleteFile(filename)
      await loadFiles()
      showToast('Email deleted successfully', 'success')
    } catch (e) {
      console.error('Delete failed', e)
      showToast('Failed to delete email', 'error')
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="flex-1">
        <div className="w-full max-w-full px-4 sm:px-6 lg:px-12 xl:px-16 py-8">
          {/* Simple Title */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-gray-900">Email Analysis</h1>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upload Section */}
            <div className="lg:col-span-1">
              <FileUploader refreshList={loadFiles} />
            </div>

            {/* Email List Section */}
            <div className="lg:col-span-2">
              {loading ? (
                <div className="card flex items-center justify-center py-12">
                  <div className="text-center">
                    <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-gray-600 font-medium">Loading emails...</p>
                  </div>
                </div>
              ) : (
                <EmailList files={files} viewAnalysis={viewAnalysis} onDelete={handleDelete} />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-16 py-4">
          <p className="text-sm text-gray-500 text-center">
            &copy; 2025 EmailGuard
          </p>
        </div>
      </footer>

      {/* Analysis Modal */}
      <ModalView
        open={modalOpen}
        close={() => {
          setModalOpen(false)
          setAnalysis(null)
        }}
        email={selectedEmail}
        analysis={analysis}
      />

      {/* Toast Notifications */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: '', type: 'success' })}
      />
    </div>
  )
}

export default App
